import React, { useState } from 'react';
import { Row, Col, Table, Card, Tag } from 'antd';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from 'recharts';
import { useLazyQuery } from '@apollo/client';
import dayjs from 'dayjs';
import { GET_CLIENT_BILLING_REPORT } from '../../../Queries/BillingReport';
import { AntdButton, DatePickerField, InputField, SelectInput } from '../../../blueplanitUtils/InputField';

const COLORS = ['#3498db', '#55ce63', '#ff9b44', '#fc6075', '#667eea', '#17a6df', '#FF5733', '#35BA67', '#9368E9', '#02a8b5'];

const formatHours = (hours) => {
  if (!hours) return '0h 0m';
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  return `${h}h ${m}m`;
};

const cardStyle = {
  borderRadius: '10px',
  border: 'none',
  boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
};

const kpiValueStyle = (color) => ({
  fontSize: '28px',
  fontWeight: 700,
  color: color || '#1a1a2e',
  lineHeight: 1.2,
});

const kpiLabelStyle = {
  fontSize: '12px',
  color: '#8c8c8c',
  fontWeight: 500,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  marginBottom: '4px',
};

const kpiIconStyle = (bg) => ({
  width: '48px',
  height: '48px',
  borderRadius: '12px',
  background: bg,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '22px',
});

const sectionTitleStyle = {
  fontSize: '14px',
  fontWeight: 600,
  color: '#1a1a2e',
  marginBottom: '12px',
};

const ClientBillingReport = () => {
  const [startDate, setStartDate] = useState(dayjs().startOf('month'));
  const [endDate, setEndDate] = useState(dayjs());
  const [clientName, setClientName] = useState('');
  const [department, setDepartment] = useState('');
  const [expandedRows, setExpandedRows] = useState([]);

  const [fetchReport, { data, loading }] = useLazyQuery(GET_CLIENT_BILLING_REPORT, {
    fetchPolicy: 'no-cache',
  });

  const handleSearch = () => {
    fetchReport({
      variables: {
        startDate: startDate.valueOf().toString(),
        endDate: endDate.valueOf().toString(),
        clientName: clientName || undefined,
        department: department || undefined,
      },
    });
  };

  const report = data?.getClientBillingReport;
  const records = report?.records || [];

  // Compute KPIs
  const totalSessions = records.reduce((s, r) => s + (r.sessionCount || 0), 0);
  const uniqueClients = new Set(records.map((r) => r.clientName).filter(Boolean)).size;
  const avgHoursPerRequest = records.length ? (report?.grandTotalHours || 0) / records.length : 0;

  // Chart: top 10 clients by hours
  const clientAgg = {};
  records.forEach((r) => {
    const name = r.clientName || 'Unknown';
    clientAgg[name] = (clientAgg[name] || 0) + (r.totalHours || 0);
  });
  const topClients = Object.entries(clientAgg)
    .map(([name, hours]) => ({ name: name.length > 15 ? name.slice(0, 15) + '...' : name, hours: Math.round(hours * 100) / 100 }))
    .sort((a, b) => b.hours - a.hours)
    .slice(0, 10);

  // Chart: department breakdown
  const deptAgg = {};
  records.forEach((r) => {
    const dept = r.department || 'Other';
    deptAgg[dept] = (deptAgg[dept] || 0) + (r.totalHours || 0);
  });
  const deptData = Object.entries(deptAgg).map(([name, value]) => ({
    name,
    value: Math.round(value * 100) / 100,
  }));

  const CustomPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    if (percent < 0.05) return null;
    return (
      <text x={x} y={y} fill="#fff" textAnchor="middle" dominantBaseline="central" fontSize={12} fontWeight={600}>
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  const columns = [
    {
      title: 'Request ID',
      dataIndex: 'requestId',
      key: 'requestId',
      width: 140,
      render: (val) => (
        <Tag style={{ borderRadius: '6px', fontWeight: 500, fontSize: '12px', background: '#f0f5ff', color: '#3498db', border: '1px solid #d6e4ff' }}>
          {val || 'N/A'}
        </Tag>
      ),
    },
    {
      title: 'Client',
      dataIndex: 'clientName',
      key: 'clientName',
      width: 200,
      render: (val) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{
            width: '32px', height: '32px', borderRadius: '50%',
            background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontSize: '13px', fontWeight: 600,
          }}>
            {(val || '?')[0]?.toUpperCase()}
          </div>
          <span style={{ fontWeight: 600, fontSize: '13px', color: '#1a1a2e' }}>{val}</span>
        </div>
      ),
    },
    {
      title: 'Department',
      dataIndex: 'department',
      key: 'department',
      width: 120,
      render: (val) => (
        <Tag color={val === 'Operations' ? 'purple' : val === 'Sales' ? 'cyan' : 'default'} style={{ borderRadius: '12px' }}>
          {val || '-'}
        </Tag>
      ),
    },
    {
      title: 'Total Hours',
      dataIndex: 'totalHours',
      key: 'totalHours',
      width: 130,
      render: (val) => <span style={{ fontWeight: 700, fontSize: '13px', color: '#3498db' }}>{formatHours(val)}</span>,
      sorter: (a, b) => a.totalSeconds - b.totalSeconds,
      defaultSortOrder: 'descend',
    },
    {
      title: 'Sessions',
      dataIndex: 'sessionCount',
      key: 'sessionCount',
      width: 90,
      render: (val) => <span style={{ fontWeight: 500 }}>{val}</span>,
    },
    {
      title: 'Team Size',
      dataIndex: 'employees',
      key: 'employees',
      width: 100,
      render: (employees) => (
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: '4px',
          background: '#f6ffed', padding: '2px 10px', borderRadius: '12px',
          color: '#55ce63', fontWeight: 600, fontSize: '12px', border: '1px solid #b7eb8f',
        }}>
          👤 {employees?.length || 0}
        </div>
      ),
    },
  ];

  const taskColumns = [
    {
      title: 'Task Type',
      dataIndex: 'taskType',
      key: 'taskType',
      width: 200,
      render: (val) => <span style={{ fontWeight: 500 }}>{val}</span>,
    },
    {
      title: 'Hours',
      dataIndex: 'totalHours',
      key: 'totalHours',
      width: 120,
      render: (val) => <span style={{ fontWeight: 600, color: '#3498db' }}>{formatHours(val)}</span>,
    },
    {
      title: 'Sessions',
      dataIndex: 'sessionCount',
      key: 'sessionCount',
      width: 80,
    },
    {
      title: 'Source',
      dataIndex: 'source',
      key: 'source',
      width: 140,
      render: (val) => {
        const label = val === 'auto-detected' ? 'Auto-Detected' : val === 'manual-inline' ? 'Manual Inline' : 'Manual';
        const color = val === 'auto-detected' ? 'blue' : val === 'manual-inline' ? 'green' : 'default';
        return <Tag color={color} style={{ borderRadius: '12px', fontWeight: 500 }}>{label}</Tag>;
      },
    },
  ];

  return (
    <div style={{ padding: '24px', background: '#f5f6fa', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ marginBottom: '20px' }}>
        <h4 style={{ fontWeight: 700, fontSize: '20px', color: '#1a1a2e', margin: 0 }}>
          Client Billing Report
        </h4>
        <p style={{ color: '#8c8c8c', fontSize: '13px', margin: '4px 0 0' }}>
          Track billable hours by client, request, and department
        </p>
      </div>

      {/* Filters */}
      <Card size="small" style={{ ...cardStyle, marginBottom: '20px' }}>
        <Row gutter={16} align="bottom">
          <Col span={5}>
            <DatePickerField label="Start Date" value={startDate} onChange={setStartDate} format="DD/MM/YYYY" />
          </Col>
          <Col span={5}>
            <DatePickerField label="End Date" value={endDate} onChange={setEndDate} format="DD/MM/YYYY" />
          </Col>
          <Col span={5}>
            <InputField
              label="Client Name"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              placeholder="Filter by client..."
            />
          </Col>
          <Col span={4}>
            <SelectInput
              label="Department"
              value={department}
              onChange={setDepartment}
              options={[
                { value: '', label: 'All Departments' },
                { value: 'Operations', label: 'Operations' },
                { value: 'Sales', label: 'Sales' },
              ]}
            />
          </Col>
          <Col span={3}>
            <AntdButton
              label={loading ? 'Loading...' : 'Generate Report'}
              color="primary"
              variant="solid"
              onClick={handleSearch}
              disabled={loading}
              style={{ marginTop: '22px', borderRadius: '8px', fontWeight: 600 }}
            />
          </Col>
        </Row>
      </Card>

      {/* KPI Cards */}
      <Row gutter={16} style={{ marginBottom: '20px' }}>
        <Col span={6}>
          <Card size="small" style={cardStyle} bodyStyle={{ padding: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={kpiLabelStyle}>Total Requests</div>
                <div style={kpiValueStyle('#1a1a2e')}>{report?.totalCount || 0}</div>
              </div>
              <div style={kpiIconStyle('linear-gradient(135deg, #667eea 0%, #764ba2 100%)')}>📋</div>
            </div>
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small" style={cardStyle} bodyStyle={{ padding: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={kpiLabelStyle}>Grand Total Hours</div>
                <div style={kpiValueStyle('#3498db')}>{formatHours(report?.grandTotalHours)}</div>
              </div>
              <div style={kpiIconStyle('linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)')}>⏱</div>
            </div>
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small" style={cardStyle} bodyStyle={{ padding: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={kpiLabelStyle}>Unique Clients</div>
                <div style={kpiValueStyle('#55ce63')}>{uniqueClients}</div>
              </div>
              <div style={kpiIconStyle('linear-gradient(135deg, #f093fb 0%, #f5576c 100%)')}>🏢</div>
            </div>
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small" style={cardStyle} bodyStyle={{ padding: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={kpiLabelStyle}>Avg Hours / Request</div>
                <div style={kpiValueStyle('#ff9b44')}>{formatHours(avgHoursPerRequest)}</div>
              </div>
              <div style={kpiIconStyle('linear-gradient(135deg, #fa709a 0%, #fee140 100%)')}>📊</div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Charts Row */}
      {records.length > 0 && (
        <Row gutter={16} style={{ marginBottom: '20px' }}>
          <Col span={16}>
            <Card size="small" style={cardStyle} bodyStyle={{ padding: '20px' }}>
              <div style={sectionTitleStyle}>Top Clients by Billable Hours</div>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={topClients} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#8c8c8c' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#8c8c8c' }} />
                  <RechartsTooltip
                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                    formatter={(value) => [`${value.toFixed(1)} hrs`, 'Hours']}
                  />
                  <Bar dataKey="hours" radius={[6, 6, 0, 0]} maxBarSize={40}>
                    {topClients.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </Col>
          <Col span={8}>
            <Card size="small" style={cardStyle} bodyStyle={{ padding: '20px' }}>
              <div style={sectionTitleStyle}>Hours by Department</div>
              {deptData.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={deptData}
                      cx="50%"
                      cy="45%"
                      innerRadius={55}
                      outerRadius={90}
                      paddingAngle={3}
                      dataKey="value"
                      labelLine={false}
                      label={CustomPieLabel}
                    >
                      {deptData.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Legend
                      verticalAlign="bottom"
                      height={36}
                      formatter={(value) => <span style={{ fontSize: '12px', color: '#555' }}>{value}</span>}
                    />
                    <RechartsTooltip
                      contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                      formatter={(value) => [`${value} hrs`, '']}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#bfbfbf' }}>
                  No department data
                </div>
              )}
            </Card>
          </Col>
        </Row>
      )}

      {/* Billing Table */}
      <Card size="small" style={cardStyle} bodyStyle={{ padding: '0' }}>
        <div style={{ padding: '16px 20px 0', ...sectionTitleStyle }}>Billing Details</div>
        <Table
          columns={columns}
          dataSource={records}
          rowKey={(r) => `${r.requestId}-${r.clientName}`}
          size="small"
          loading={loading}
          pagination={{ pageSize: 50, style: { padding: '0 16px' } }}
          locale={{ emptyText: 'Select date range and click Generate Report to load data' }}
          expandable={{
            expandedRowKeys: expandedRows,
            onExpandedRowsChange: setExpandedRows,
            expandedRowRender: (record) => (
              <div style={{ padding: '12px 20px', background: '#fafbfc' }}>
                <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px', color: '#1a1a2e' }}>
                  Task Breakdown
                </div>
                <Table
                  columns={taskColumns}
                  dataSource={record.tasks}
                  rowKey={(t) => `${t.taskType}-${t.source}`}
                  size="small"
                  pagination={false}
                />
              </div>
            ),
          }}
          summary={() =>
            records.length ? (
              <Table.Summary fixed>
                <Table.Summary.Row style={{ background: '#fafbfc' }}>
                  <Table.Summary.Cell index={0} colSpan={3}>
                    <strong style={{ fontSize: '13px' }}>Grand Total</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={3}>
                    <strong style={{ color: '#3498db', fontSize: '13px' }}>{formatHours(report?.grandTotalHours)}</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={4}>
                    <strong>{totalSessions}</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={5} />
                </Table.Summary.Row>
              </Table.Summary>
            ) : null
          }
        />
      </Card>
    </div>
  );
};

export default ClientBillingReport;
